import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LoginContext } from '../../reactcontext/ReactContext'
import axios from 'axios'
import swal from 'sweetalert'
import { MedicalEditModal } from './tables/MedicalTableInit'
import PassportTable from './tables/PassportTable'
import logo from '../../assets/logo.avif'

const NeedMC = () => {
  const apiUrl = process.env.REACT_APP_API_URL
  const { loginInfo } = useContext(LoginContext)
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRow, setSelectedRow] = useState(null)
  const [activeFilter, setActiveFilter] = useState('needmc')

  const printedBy = loginInfo?.userDetails
    ? `${loginInfo.userDetails.firstName} ${loginInfo.userDetails.lastName}`
    : 'Admin'

  useEffect(() => {
    if (!loginInfo.login) navigate('/')
  }, [loginInfo, navigate])

  const fetchRows = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${apiUrl}/employee/medicalList`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      })
      if (res.data.error) {
        swal({ icon: 'error', title: 'Error', text: res.data.error })
        setRows([])
      } else {
        setRows(res.data.passports || [])
      }
    } catch (err) {
      swal({ icon: 'error', title: 'Error', text: err.response?.data?.error || err.message })
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [apiUrl])

  useEffect(() => { fetchRows() }, [fetchRows])

  function getLatestVisa(row) {
    const visas = row.Employee?.Visas
    if (!visas || visas.length === 0) return null
    return visas.reduce((a, b) =>
      new Date(a.expiryDate) > new Date(b.expiryDate) ? a : b
    )
  }

  function getVisaExpireDate(row) {
    const visa = getLatestVisa(row)
    return visa?.expiryDate ? visa.expiryDate.split('T')[0] : 'N/A'
  }

  function getDaysLeft(row) {
    const visa = getLatestVisa(row)
    if (!visa?.expiryDate) return 'N/A'
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const expiry = new Date(visa.expiryDate)
    expiry.setHours(0, 0, 0, 0)
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
  }

  function getReadableTimestamp() {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`
  }

  const handleExportPDF = (data) => {
    if (!data || data.length === 0) return
    const html2pdf = require('html2pdf.js')
    const tdStyle = 'padding:4px 6px;border:1px solid #e5e7eb;'
    const rowsHtml = data.map((r, i) => {
      const days = getDaysLeft(r)
      return `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="${tdStyle}">${r.Employee ? `${r.Employee.firstName} ${r.Employee.lastName}` : 'N/A'}</td>
        <td style="${tdStyle}">${r.passportNo || 'N/A'}</td>
        <td style="${tdStyle}">${getVisaExpireDate(r)}</td>
        <td style="${tdStyle}">${days === 'N/A' ? 'N/A' : days < 0 ? `Expired ${Math.abs(days)} days ago` : `${days} days`}</td>
        <td style="${tdStyle}">${r.Employee?.Health?.mcStatus || 'N/A'}</td>
      </tr>`
    }).join('')
    const el = document.createElement('div')
    el.style.cssText = 'padding:20px;font-family:Arial,sans-serif;font-size:11px;'
    el.innerHTML = `
      <div style="text-align:center;margin-bottom:14px;">
        <img src="${logo}" style="width:60px;display:block;margin:0 auto 6px auto;" crossorigin="anonymous"/>
        <div style="font-size:16px;font-weight:bold;margin:4px 0;">Rahman Group</div>
        <div style="font-size:13px;margin:2px 0;">Report Listing - Need Medical Certificate</div>
      </div>
      <div style="display:flex;gap:30px;flex-wrap:wrap;margin-bottom:12px;">
        <span><em>Printed By:</em> <strong>${printedBy}</strong></span>
        <span><em>Printed On:</em> <strong>${getReadableTimestamp()}</strong></span>
        <span><em>Total Records:</em> <strong>${data.length}</strong></span>
        <span><em>Signature:</em> <strong>______________</strong></span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead><tr style="background:#e5e7eb;">
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Employee Name</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Passport</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Visa Expire Date</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Days Left</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Status</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>`
    html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
    }).from(el).outputPdf('blob').then(blob => {
      window.open(URL.createObjectURL(blob), '_blank')
    })
  }

  const columns = useMemo(() => [
    {
      Header: 'Employee Name',
      accessor: row => row.Employee ? `${row.Employee.firstName} ${row.Employee.lastName}` : 'N/A',
    },
    {
      Header: 'Passport',
      accessor: row => row.passportNo || 'N/A',
    },
    {
      Header: 'Visa Expire Date',
      accessor: row => getVisaExpireDate(row),
    },
    {
      Header: 'Days Left',
      accessor: row => {
        const days = getDaysLeft(row)
        if (days === 'N/A') return 'N/A'
        return days
      },
      Cell: ({ value }) => {
        if (value === 'N/A') return <span className='text-gray-400'>N/A</span>
        if (value < 0) return <span className='text-red-500 font-semibold'>Expired {Math.abs(value)}d ago</span>
        if (value <= 30) return <span className='text-orange-500 font-semibold'>{value} days</span>
        return <span className='text-green-600 font-semibold'>{value} days</span>
      },
    },
    {
      Header: 'Status',
      accessor: row => row.Employee?.Health?.mcStatus || 'N/A',
    },
    {
      Header: 'Action',
      accessor: 'action',
      Cell: ({ row }) => (
        <button
          className='px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700'
          onClick={() => setSelectedRow(row.original)}
        >
          Edit
        </button>
      ),
    },
  ], [])

  const DONE_STATUSES = ['Approved', 'Pending', 'Rejected']

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      const mcStatus = r.Employee?.Health?.mcStatus
      if (mcStatus && DONE_STATUSES.includes(mcStatus)) return false
      const days = getDaysLeft(r)
      if (days === 'N/A') return false
      if (activeFilter === 'expired') return days < 0
      return days >= 0 && days <= 90
    })
  }, [rows, activeFilter])

  return (
    <div className='w-full flex h-full flex-col items-center justify-between relative gap-3'>
      <div className='self-start'>
        <Link to={'/dashboard'} className='text-yellow-700'>Dashboard</Link>
        {' > '}
        <Link to={'/medical'} className='text-yellow-700'>Medical</Link>
        {' > '} Need MC
      </div>

      <div className='w-full p-2 flex items-center flex-col justify-center h-full gap-3'>
        <h1 className='text-xl font-semibold'>Need Medical Certificate</h1>
        <div className='w-full h-full overflow-auto'>
          <MedicalEditModal
            selectedRow={selectedRow}
            onClose={() => setSelectedRow(null)}
            onSaved={fetchRows}
          />
          {loading ? (
            <p className='w-full text-center text-lg py-5'>Fetching data... Please wait</p>
          ) : (
            <PassportTable
              columns={columns}
              data={filteredRows}
              onExportPDF={handleExportPDF}
              extraControls={
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setActiveFilter('needmc')}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium border transition-colors ${
                      activeFilter === 'needmc'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    Need MC
                  </button>
                  <button
                    onClick={() => setActiveFilter('expired')}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium border transition-colors ${
                      activeFilter === 'expired'
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    Expired
                  </button>
                </div>
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default NeedMC
